import Component from '@glimmer/component';
import type { ComponentLike } from '@glint/template';
// ---------------------------------------------------------------------------
// FilterTabs – horizontal pill-style filter buttons with active state
// ---------------------------------------------------------------------------

export interface FilterTab {
  id: string;
  label: string;
  count?: number;
  icon?: ComponentLike<{ Element: HTMLElement; Args: { class?: string } }>;
}

interface FilterTabsSignature {
  Args: {
    /** Array of tab definitions */
    tabs: FilterTab[];
    /** ID of the currently active tab */
    activeId?: string;
    /** Callback when a tab is selected */
    onSelect?: (id: string) => void;
    /** Custom class for the active tab pill */
    activeClass?: string;
    /** Custom class for inactive tab pills */
    inactiveClass?: string;
    /** Custom class for count badge on active tab */
    countActiveClass?: string;
    /** Custom class for count badge on inactive tabs */
    countInactiveClass?: string;
    /** Whether to show count badges (default: true) */
    showCounts?: boolean;
    /** Optional selected-state ARIA attribute contract */
    selectedStateAttribute?: 'aria-pressed' | 'aria-selected';
  };
  Element: HTMLDivElement;
}

export default class FilterTabs extends Component<FilterTabsSignature> {
  get activeClass() {
    return this.args.activeClass ?? 'tio-filter-tabs__tab--active';
  }

  get inactiveClass() {
    return (
      this.args.inactiveClass ??
      'tio-bg-surface tio-fg-secondary hover:tio-bg-sunken border tio-border-default'
    );
  }

  get countActiveClass() {
    return this.args.countActiveClass ?? 'tio-filter-tabs__count--active';
  }

  get countInactiveClass() {
    return this.args.countInactiveClass ?? 'tio-bg-chip';
  }

  get showCounts() {
    return this.args.showCounts !== false;
  }

  get selectedStateAttribute() {
    return this.args.selectedStateAttribute;
  }

  handleSelect = (id: string) => {
    this.args.onSelect?.(id);
  };

  <template>
    <div
      class="tio-wrap-tight"
      role={{if (eq this.selectedStateAttribute "aria-selected") "tablist"}}
      ...attributes
    >
      {{#each @tabs as |tab|}}
        <button
          type="button"
          role={{if (eq this.selectedStateAttribute "aria-selected") "tab"}}
          aria-selected={{if
            (eq this.selectedStateAttribute "aria-selected")
            (if (eq @activeId tab.id) "true" "false")
          }}
          aria-pressed={{if
            (eq this.selectedStateAttribute "aria-pressed")
            (if (eq @activeId tab.id) "true" "false")
          }}
          class="px-4 py-2 rounded-full tio-text-sm-medium transition-all
            {{if (eq @activeId tab.id) this.activeClass this.inactiveClass}}"
          {{on "click" (fn this.handleSelect tab.id)}}
        >
          {{#if tab.icon}}
            {{#let tab.icon as |Icon|}}
              <Icon class="mr-2 inline-flex" />
            {{/let}}
          {{/if}}
          {{tab.label}}
          {{#if this.showCounts}}
            {{#if tab.count}}
              <span
                class="ml-1.5 px-1.5 py-0.5 rounded-full text-xs
                  {{if (eq @activeId tab.id) this.countActiveClass this.countInactiveClass}}"
              >
                {{tab.count}}
              </span>
            {{/if}}
          {{/if}}
        </button>
      {{/each}}
    </div>
  </template>
}
